import { apiFetch } from '@/api/apiFetch';
import { normalizeUserRole, type UserProfile } from '@/api/user';

// A session probe that never settles freezes the whole app behind a loading
// screen, so it is capped: an unreachable backend resolves to "signed out",
// which lands the user on the welcome screen where they can retry.
const SESSION_PROBE_TIMEOUT_MS = 15000;

/**
 * Returns the current authenticated user's Khova profile (session cookie on web, bearer on
 * native, refreshed once on 401).
 * Returns null if not authenticated, or if the session cannot be resolved.
 */
export const getCurrentUserProfile = async (signal?: AbortSignal): Promise<UserProfile | null> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), SESSION_PROBE_TIMEOUT_MS);
  signal?.addEventListener('abort', () => controller.abort());

  try {
    const res = await apiFetch('/auth/me', {
      method: 'GET',
      signal: controller.signal,
    });

    if (res.status === 401 || !res.ok) {
      return null;
    }

    const raw = (await res.json()) as Omit<UserProfile, 'role'> & { role?: unknown };
    return { ...raw, role: normalizeUserRole(raw.role) };
  } catch {
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};
